import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import apiService from '../services/api';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar token existente ao carregar a aplicação
  useEffect(() => {
    const checkAuthState = async () => {
      console.log('🔍 Verificando estado de autenticação...');
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        console.log('⚠️ Nenhum token encontrado no localStorage');
        setIsLoading(false);
        return;
      }

      console.log('🔑 Token encontrado, validando...');
      try {
        const response = await apiService.validateToken();
        console.log('✅ Resposta da validação:', response);
        
        if (response.success && response.data) {
          console.log('🎉 Token válido, autenticando usuário:', response.data);
          setUser(response.data);
          setIsAuthenticated(true);
        } else {
          console.log('❌ Token inválido, removendo...');
          localStorage.removeItem('admin_token');
        }
      } catch (err) {
        console.error('💥 Erro ao validar token:', err);
        localStorage.removeItem('admin_token');
      } finally {
        console.log('✅ Verificação de auth concluída');
        setIsLoading(false);
      }
    };

    checkAuthState();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      console.log('🔐 Iniciando login...', { username, password: password ? '***' : 'empty' });
      setIsLoading(true);
      setError(null);

      console.log('🚀 Chamando apiService.login...');
      const response = await apiService.login(username, password);
      console.log('✅ Resposta recebida:', response);
      
      if (response.success) {
        console.log('🎉 Login bem-sucedido, salvando token...');
        localStorage.setItem('admin_token', response.token);
        setUser(response.user);
        setIsAuthenticated(true);
        console.log('✅ Estado de autenticação atualizado');
      } else {
        console.log('❌ Login falhou:', response.message);
        throw new Error(response.message || 'Erro no login');
      }
    } catch (err: any) {
      console.log('💥 Erro capturado:', err);
      console.log('💥 Erro detalhado:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      const errorMessage = err.response?.data?.message || err.message || 'Erro no login';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Tentar fazer logout no servidor
      await apiService.logout();
    } catch (err) {
      console.error('Erro ao fazer logout no servidor:', err);
    } finally {
      // Limpar estado local independentemente do resultado
      localStorage.removeItem('admin_token');
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
